import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { telephoneNoMask } from "../../functions/mask";
import { getWeekDay, translateWeekday } from "../../functions/util";

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  hours: HourType[];
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
};

type BookingType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  email: string;
  date: string;
  bookingHourId: string;
  bookingEnvironmentId: string;
};

export function BookingUpdate() {
  const { pluginspace_id, id } = useParams();
  const { registerAccess, getToken, getThisPluginspace } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateBooking, setDateBooking] = useState("");
  const [hours, setHours] = useState([
    { value: 0, select: "Selecione um horário" },
  ]);
  const [hourId, setHourId] = useState(0);
  const [environmentId, setEnvironmentId] = useState(0);
  const [people, setPeople] = useState(0);
  const [phone, setPhone] = useState("");
  const [maskTelephone, setMaskTelephone] = useState("(99) 9999-9999");

  useEffect(() => {
    registerAccess("booking/update");
    init();
  }, []);

  async function init() {
    setLoading(true);
    await getConfig();
    await getBookingConfig();
    setLoading(false);
  }

  async function getBooking(bookingConfigItem: BookingConfigType) {
    await api
      .post(
        "/api/queueReservation/booking/filter",
        {
          id,
          bookingConfigId: bookingConfigItem.id,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((responseBooking) => {
        const bookingItem = responseBooking.data;
        if (bookingItem && bookingItem.data && bookingItem.data[0]) {
          const booking: BookingType = bookingItem.data[0];
          setDateBooking(booking.date);
          changeDate(
            booking.date,
            parseInt(booking.bookingHourId),
            bookingConfigItem
          );
          setEnvironmentId(parseInt(booking.bookingEnvironmentId));
          setPeople(booking.people);
          setName(booking.name);
          if (booking.phone.length >= 11) {
            setMaskTelephone("(99) 99999-9999");
          }
          setPhone(booking.phone);
          setEmail(booking.email);
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao encontrar reserva!",
            text: "Não foi possível encontrar reserva.",
          }).then(() => navigate(`/${pluginspace_id}/booking`));
        }
      })
      .catch((err) => {
        console.log(err);
        let title = "Erro ao encontrar reserva!";
        let text = "Não foi possível encontrar reserva.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(() => navigate(`/${pluginspace_id}/booking`));
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem: BookingConfigType = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);
            await getBooking(bookingConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reserva configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  function filterHour(hour: string, date: string) {
    const now = new Date().getTime();
    const timeToCompare = new Date(`${date} ${hour}`).getTime();
    if (now > timeToCompare) return false;
    return true;
  }

  async function changeBooking() {
    if (people <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `Precisa haver uma pessoa ou mais na reserva.`,
      });
      return;
    }

    if (!name) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um nome para reserva.`,
      });
      return;
    }

    if (!name.split(" ")[1]) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um sobrenome para reserva.`,
      });
      return;
    }

    if (bookingConfig?.maxPeople && people > bookingConfig.maxPeople) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        html: `Há mais pessoas do que o permitido na reserva<br>(Máximo ${bookingConfig.maxPeople})`,
      });
      return;
    }
    if (!hourId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um horário para reserva`,
      });
      return;
    }
    if (!environmentId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um ambiente para reserva`,
      });
      return;
    }

    if (!email.includes("@")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `E-mail inválido`,
      });
      return;
    }

    if (!bookingConfig?.id) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: `Erro ao encontrar configuração de reserva`,
      });
      return;
    }
    const hour = hours.find((h) => h.value === hourId);
    setLoading(true);
    const tk = await getToken();
    const pg = await getThisPluginspace(tk ? tk : "");
    if (pg && pg.gmt && hour?.select) {
      setLoading(true);
      const date = new Date(
        `${new Date().toString().split("GMT")[0]}${pg.gmt}`
      );
      if (
        date.getTime() >
        new Date(`${dateBooking} ${hour.select} GMT${pg.gmt}`).getTime()
      ) {
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: `Horário de reserva indisponível`,
        });
        setLoading(false);
        return;
      }
    }
    await api
      .put(
        `/api/queueReservation/booking/${id}`,
        {
          date: dateBooking,
          bookingHourId: hourId,
          bookingEnvironmentId: environmentId,
          email,
          bookingConfigId: bookingConfig.id,
          people,
          name,
          phone: telephoneNoMask(phone),
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        const bookingResponse = response.data;
        if (bookingResponse.success) {
          Swal.fire({
            icon: "success",
            title: "Sucesso!",
            text: "Reserva atualizada.",
          }).then(() =>
            navigate(`/${pluginspace_id}/booking/${telephoneNoMask(phone)}`)
          );
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao atualizar reserva!",
            text: "Não foi possível atualizar reserva.",
          }).then(async () => await init());
        }
      })
      .catch((err) => {
        setLoading(false);
        let title = "Erro ao atualizar reserva!";
        let text = "Não foi possível atualizar reserva.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(async () => await init());
      });
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  function changePhone(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function changeDate(
    data: string,
    hour?: number,
    bookingConfigItem?: BookingConfigType
  ) {
    const config = bookingConfigItem ? bookingConfigItem : bookingConfig;
    if (hour) setHourId(hour);
    else setHourId(0);

    const fullData = new Date(data);
    const week = getWeekDay(fullData.getDay());
    let newHours = [{ value: 0, select: "Selecione um horário" }];

    if (config?.days) {
      const hoursFind = config.days.find((day) => day.day === week);
      if (
        hoursFind &&
        hoursFind.able &&
        hoursFind.hours &&
        hoursFind.hours.length > 0
      ) {
        const hoursNext = hoursFind.hours
          .map((hour) => ({ value: parseInt(hour.id), select: hour.hour }))
          .sort((a, b) =>
            parseInt(a.select.split(":")[0]) > parseInt(b.select.split(":")[0])
              ? 1
              : parseInt(b.select.split(":")[0]) >
                parseInt(a.select.split(":")[0])
              ? -1
              : 0
          )
          .filter((hour) => filterHour(hour.select, data));
        setHours([...newHours, ...hoursNext]);
        setDateBooking(data);
        return;
      }
    }
    Swal.fire({
      icon: "warning",
      title: "Atenção!",
      text: `Não há horários disponíveis para ${translateWeekday(week)}.`,
    });
  }

  return (
    <div className="booking-update">
      {loading ? (
        <div style={{ width: "100%", height: "calc(100vh)" }}>
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      ) : (
        <div className="content-booking-update">
          {bookingConfig && (
            <div className="body-booking-update">
              {bookingConfig.images && bookingConfig.images[0] && (
                <div className="booking-image">
                  <img
                    src={bookingConfig.images[0].url}
                    alt="Imagem Reserva 1"
                  />
                </div>
              )}
              <div className="booking-form-update">
                <h2>Reserva</h2>
                <div className="multiple-input">
                  <div className="input" style={{ flex: 1 }}>
                    <label>Data</label>
                    <input
                      type="date"
                      value={dateBooking}
                      onChange={(event) => changeDate(event.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="input" style={{ flex: 1 }}>
                    <label>Horas</label>
                    <select
                      value={hourId}
                      onChange={(event) =>
                        setHourId(parseInt(event.target.value))
                      }
                    >
                      {hours.map((hour) => (
                        <option value={hour.value}>{hour.select}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="multiple-input">
                  <div className="input" style={{ flex: 1 }}>
                    <label>Ambiente</label>
                    {bookingConfig.environment &&
                    bookingConfig.environment.length > 0 ? (
                      <select
                        value={environmentId}
                        onChange={(event) =>
                          setEnvironmentId(parseInt(event.target.value))
                        }
                      >
                        <option value={0}>Escolha um ambiente</option>
                        {bookingConfig.environment.map((environment) => (
                          <option value={environment.id}>
                            {environment.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={environmentId}
                        onChange={(event) =>
                          setEnvironmentId(parseInt(event.target.value))
                        }
                      >
                        <option value={0}>Escolha um ambiente</option>
                      </select>
                    )}
                  </div>
                  <div className="input" style={{ flex: 1 }}>
                    <label>Número de pessoas</label>
                    <input
                      type="number"
                      value={people === 0 ? "" : people}
                      placeholder="0"
                      onChange={(event) =>
                        setPeople(parseInt(event.target.value))
                      }
                      max={bookingConfig.maxPeople}
                    />
                  </div>
                </div>
                <div className="input">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => {
                      const isValidName = /^[a-zA-ZÀ-ÿ\s]*$/.test(
                        event.target.value
                      );
                      if (isValidName) setName(event.target.value);
                    }}
                  />
                </div>
                <div className="input">
                  <label>Celular</label>
                  <InputMask
                    mask={maskTelephone}
                    type="text"
                    value={phone}
                    disabled
                    onBeforeInputCapture={(event) =>
                      changePhone(event.currentTarget.value)
                    }
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </div>
                <div className="input">
                  <label>Email</label>
                  <input
                    type="text"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <div className="button-booking-update">
                  <MainButton
                    color={
                      config?.webappcolor.fontbutton
                        ? config.webappcolor.fontbutton
                        : "#636363"
                    }
                    backgroundColor={
                      config?.webappcolor.webappbutton
                        ? config.webappcolor.webappbutton
                        : "#EAEAEA"
                    }
                    text="Atualizar reserva →"
                    onClick={() => changeBooking()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
